import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus } from "lucide-react";
import {
  useReactTable,
  getCoreRowModel,
  createColumnHelper,
  flexRender,
} from "@tanstack/react-table";
import { useBreadcrumb } from "@/contexts/BreadcrumbContext";

interface Tour {
  _id: string;
  name: string;
}

const columnHelper = createColumnHelper<Tour>();

const columns = [
  columnHelper.accessor("name", {
    header: "Name",
    cell: (info) => info.getValue(),
  }),
];

export function TourList() {
  const navigate = useNavigate();
  const [tours, setTours] = useState<Tour[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [tourName, setTourName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { setBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();

  useEffect(() => {
    setBreadcrumbs([
      { label: "Tours", path: "/tours" },
    ]);

    return () => {
      clearBreadcrumbs();
    };
  }, []);

  useEffect(() => {
    async function fetchTours() {
      try {
        const response = await fetch("/api/tours");
        if (response.ok) {
          const data = await response.json();
          setTours(data.tours || []);
        }
      } catch (error) {
        console.error("Failed to fetch tours:", error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchTours();
  }, []);

  async function handleCreateTour() {
    if (!tourName.trim()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch("/api/tours", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ name: tourName.trim() }),
      });

      if (response.ok) {
        const data = await response.json();
        setTours([...tours, data.tour]);
        setIsDrawerOpen(false);
        setTourName("");
      } else {
        console.error("Failed to create tour");
      }
    } catch (error) {
      console.error("Failed to create tour:", error);
    } finally {
      setIsSubmitting(false);
    }
  }

  const table = useReactTable({
    data: tours,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-semibold">Tours</h1>
        <Button onClick={() => setIsDrawerOpen(true)}>
          <Plus className="h-4 w-4" />
          Create
        </Button>
      </div>

      <Drawer open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
        <DrawerContent className="max-w-2xl mx-auto">
          <DrawerHeader>
            <DrawerTitle>Create Tour</DrawerTitle>
          </DrawerHeader>
          <div className="p-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="tour-name">Tour Name</Label>
              <Input
                id="tour-name"
                value={tourName}
                onChange={(e) => setTourName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && tourName.trim() && !isSubmitting) {
                    handleCreateTour();
                  }
                }}
                autoFocus
                placeholder="Enter tour name"
                disabled={isSubmitting}
              />
            </div>
          </div>
          <DrawerFooter className="flex-row gap-2 justify-center">
            <DrawerClose asChild>
              <Button variant="outline" disabled={isSubmitting}>
                Cancel
              </Button>
            </DrawerClose>
            <Button
              onClick={handleCreateTour}
              disabled={!tourName.trim() || isSubmitting}
            >
              {isSubmitting ? "Creating..." : "Create"}
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
      {isLoading ? (
        <div>Loading...</div>
      ) : (
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                  onClick={() => navigate(`/tours/${row.original._id}`)}
                  className="cursor-pointer"
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  No tours found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

